import dayjs from 'dayjs'
import type { TFunction } from 'i18next'

import { formatLongDate, formatTime } from '#/shared/lib/format'

/**
 * Resolve an ISO timestamp to a date-group label: "Today" / "Yesterday" for the
 * two most recent days, otherwise an absolute date ("Jun 6, 2026"). Takes `t`
 * so the function stays UI-free while still returning display-ready text — the
 * activity list groups consecutive rows by this exact string.
 */
export function dateGroupLabel(
  iso: string | null | undefined,
  t: TFunction,
): string {
  if (!iso) return ''
  const day = dayjs(iso)
  if (!day.isValid()) return ''
  const now = dayjs()
  if (day.isSame(now, 'day')) return t('phonePanel.date.today')
  if (day.isSame(now.subtract(1, 'day'), 'day')) {
    return t('phonePanel.date.yesterday')
  }
  return formatLongDate(iso)
}

/** Right-aligned clock label for a row, e.g. "9:45 AM". */
export function timeLabel(iso: string | null | undefined): string {
  return iso ? formatTime(iso) : ''
}

/**
 * Relative timestamp for an activity-list row: the time of day if it's today
 * ("9:45 AM"), the weekday for anything within the last week ("Sat"), and a
 * numeric `DD/MM/YY` date ("10/06/26") once it's older than a week.
 */
export function activityTimestamp(iso: string | null | undefined): string {
  if (!iso) return ''
  const day = dayjs(iso)
  if (!day.isValid()) return ''
  const now = dayjs()
  if (day.isSame(now, 'day')) return formatTime(iso)
  if (day.isAfter(now.subtract(7, 'day').startOf('day'))) {
    return day.format('ddd')
  }
  return day.format('DD/MM/YY')
}
